import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Download, MessageCircle, Loader2,
  AlertTriangle, FileText, Globe, 
  Calendar, Target, Shield, DollarSign,
  CheckSquare, Clock, AlertCircle, 
  BookOpen, ArrowRight, Award, Phone
} from 'lucide-react';
import { LogoFull, LogoMark } from '../components/LogoFull';
import { getReport, getServices } from '../lib/api';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const WHATSAPP_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER || '34607282943';

// Section icons mapping
const sectionIcons = {
  'resumo': Target,
  'análise': FileText,
  'rota': ArrowRight,
  'alternativa': Shield,
  'checklist': CheckSquare,
  'cronograma': Calendar,
  'riscos': AlertCircle,
  'fontes': BookOpen,
  'custos': DollarSign
};

// Parse report into structured sections
const parseReportSections = (reportText) => {
  if (!reportText) return [];
  
  const sections = [];
  const lines = reportText.split('\n');
  let currentSection = null;
  let currentContent = [];

  const sectionPatterns = [
    { pattern: /resumo\s*executivo/i, title: 'Resumo Executivo', icon: 'resumo' },
    { pattern: /leitura\s*do\s*caso|análise\s*do\s*perfil/i, title: 'Análise do Perfil', icon: 'análise' },
    { pattern: /rota\s*principal/i, title: 'Rota Principal Recomendada', icon: 'rota' },
    { pattern: /rota\s*alternativa|alternativa\s*estratégica/i, title: 'Alternativa Estratégica', icon: 'alternativa' },
    { pattern: /checklist|documentos/i, title: 'Checklist Documental', icon: 'checklist' },
    { pattern: /cronograma/i, title: 'Cronograma Sugerido', icon: 'cronograma' },
    { pattern: /riscos|mitigação|pontos\s*críticos/i, title: 'Riscos e Pontos Críticos', icon: 'riscos' },
    { pattern: /custos|estimativa/i, title: 'Estimativa de Investimento', icon: 'custos' },
    { pattern: /fontes|próximo\s*passo/i, title: 'Fontes Públicas Consultadas', icon: 'fontes' }
  ];

  for (const line of lines) {
    const cleanLine = line.replace(/[#*]/g, '').trim();
    
    // Check if line is a section header
    let foundSection = false;
    for (const { pattern, title, icon } of sectionPatterns) {
      if (pattern.test(cleanLine) && cleanLine.length < 80) {
        // Save previous section
        if (currentSection) {
          sections.push({
            ...currentSection,
            content: currentContent.join('\n').trim()
          });
        }
        currentSection = { title, icon };
        currentContent = [];
        foundSection = true;
        break;
      }
    }

    if (!foundSection && currentSection) {
      currentContent.push(line);
    } else if (!foundSection && !currentSection && cleanLine) {
      // Content before first section - add to intro
      if (!sections.find(s => s.title === 'Resumo Executivo')) {
        currentSection = { title: 'Resumo Executivo', icon: 'resumo' };
        currentContent.push(line);
      }
    }
  }

  // Add last section
  if (currentSection) {
    sections.push({
      ...currentSection,
      content: currentContent.join('\n').trim()
    });
  }

  return sections;
};

// Format content for professional display
const formatSectionContent = (content) => {
  if (!content) return [];
  
  const lines = content.split('\n').filter(l => l.trim());
  const formatted = [];

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    // Remove markdown formatting
    let cleanLine = trimmed
      .replace(/^\*\*(.+)\*\*$/, '$1')
      .replace(/\*\*(.+?)\*\*/g, '$1')
      .replace(/^\*\s*/, '')
      .replace(/^-\s*/, '')
      .replace(/^•\s*/, '')
      .replace(/^\d+\.\s*/, '')
      .replace(/^\d+\)\s*/, '');

    if (cleanLine.length > 3) {
      formatted.push(cleanLine);
    }
  }

  return formatted;
};

export default function ReportPage() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [reportData, servicesData] = await Promise.all([
          getReport(caseId),
          getServices()
        ]);
        
        if (reportData.status !== 'generated' && reportData.status !== 'emailed') {
          navigate(`/preview/${caseId}`);
          return;
        }
        
        setCaseData(reportData);
        setServices(servicesData);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Relatório não encontrado');
      } finally {
        setLoading(false);
      }
    };

    if (caseId) {
      fetchData();
    }
  }, [caseId, navigate]);

  const handleWhatsAppClick = (type = 'support') => {
    const serviceName = selectedService ? 
      services.find(s => s.code === selectedService)?.name_pt || selectedService 
      : 'Consulta';
    
    const message = type === 'support' 
      ? `Olá, preciso de suporte referente ao meu relatório LEGISLAI (ID ${caseId}). Código: ${caseData?.coupon_code || 'N/A'}.`
      : `Olá, recebi meu relatório LEGISLAI (ID ${caseId}). Código: ${caseData?.coupon_code || 'N/A'}. Gostaria de avançar com o serviço: ${serviceName}.`;
    
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`, '_blank');
  };

  const handleDownloadPDF = () => {
    window.open(`https://legal-report-gen.emergent.host/api/pdf/${caseId}`, '_blank');
  };

  const copyCouponCode = () => {
    if (caseData?.coupon_code) {
      navigator.clipboard.writeText(caseData.coupon_code);
      toast.success('Código copiado');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050608] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#C9A24A] animate-spin" />
      </div>
    );
  }

  if (error || !caseData) {
    return (
      <div className="min-h-screen bg-[#050608] flex items-center justify-center px-6">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl text-white mb-2">Documento não localizado</h1>
          <p className="text-gray-400 mb-6">{error}</p>
          <button onClick={() => navigate('/')} className="px-8 py-3 bg-[#C9A24A] text-[#050608] font-semibold uppercase tracking-wider text-sm">
            Retornar
          </button>
        </div>
      </div>
    );
  }

  const reportSections = parseReportSections(caseData.report_html);
  const reportDate = caseData.created_at ? new Date(caseData.created_at).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  }) : new Date().toLocaleDateString('pt-BR');

  return (
    <div className="min-h-screen bg-[#050608]">
      {/* Executive Report Container */}
      <div className="max-w-4xl mx-auto">
        
        {/* ===== COVER PAGE ===== */}
        <section className="min-h-screen flex flex-col justify-between px-8 py-12 border-b border-[#1a1d24]">
          
          {/* Top Bar */}
          <div className="flex items-center justify-between">
            <LogoFull size="default" />
            <div className="text-right">
              <span className="text-[#C9A24A] text-sm font-semibold tracking-wider">
                VALOR DO RELATÓRIO
              </span>
              <p className="text-white text-2xl font-bold">R$49,90</p>
            </div>
          </div>

          {/* Center Content */}
          <div className="flex-1 flex flex-col items-center justify-center text-center py-16">
            {/* Logo Mark */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
            >
              <LogoMark size={80} className="mb-8" />
            </motion.div>

            {/* Title Block */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl text-white mb-4 tracking-wide">
                Relatório Estratégico
              </h1>
              <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#C9A24A] mb-8">
                Personalizado
              </h2>
            </motion.div>

            {/* Seal */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="inline-flex items-center gap-3 px-6 py-3 border border-[#C9A24A]/30 rounded-sm bg-[#C9A24A]/5 mb-12"
            >
              <Award className="w-5 h-5 text-[#C9A24A]" />
              <span className="text-[#C9A24A] text-sm font-semibold tracking-widest uppercase">
                Análise Técnica Personalizada
              </span>
            </motion.div>

            {/* Client Info Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="w-full max-w-md bg-[#0a0c10] border border-[#1a2030] p-8"
            >
              <div className="space-y-4 text-left">
                <div className="flex justify-between items-center pb-3 border-b border-[#1a2030]">
                  <span className="text-gray-500 text-sm uppercase tracking-wider">ID do Caso</span>
                  <span className="text-[#C9A24A] font-mono font-bold text-lg">{caseId}</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-[#1a2030]">
                  <span className="text-gray-500 text-sm uppercase tracking-wider">Data de Emissão</span>
                  <span className="text-white">{reportDate}</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-[#1a2030]">
                  <span className="text-gray-500 text-sm uppercase tracking-wider">Cliente</span>
                  <span className="text-white font-medium">{caseData.nome || 'N/A'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 text-sm uppercase tracking-wider">Nacionalidade</span>
                  <span className="text-white">{caseData.nationality || 'N/A'}</span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Bottom - Download Button */}
          <div className="text-center">
            <button
              data-testid="download-pdf-cover"
              onClick={handleDownloadPDF}
              className="inline-flex items-center gap-3 px-8 py-4 bg-[#C9A24A] text-[#050608] font-semibold uppercase tracking-wider text-sm hover:bg-white transition-colors duration-300"
            >
              <Download className="w-5 h-5" />
              Baixar Versão PDF
            </button>
          </div>
        </section>

        {/* ===== REPORT CONTENT ===== */}
        <main className="px-8 py-16">
          
          {/* Section Header */}
          <div className="mb-12 pb-6 border-b border-[#1a2030]">
            <span className="text-[#C9A24A] text-sm uppercase tracking-widest">Conteúdo do Relatório</span>
            <h3 className="font-['Playfair_Display'] text-2xl text-white mt-2">
              Análise Completa do Caso
            </h3>
          </div>

          {/* Report Sections */}
          <div className="space-y-8">
            {reportSections.map((section, index) => {
              const IconComponent = sectionIcons[section.icon] || FileText;
              const contentLines = formatSectionContent(section.content);
              
              if (contentLines.length === 0) return null;

              return (
                <motion.article
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  viewport={{ once: true }}
                  className="bg-[#0a0c10] border border-[#1a2030] hover:border-[#2a3040] transition-colors duration-300"
                >
                  {/* Section Header */}
                  <div className="flex items-center gap-4 px-6 py-4 border-b border-[#1a2030] bg-[#0d1015]">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#C9A24A]/10 rounded-sm">
                      <IconComponent className="w-5 h-5 text-[#C9A24A]" />
                    </div>
                    <h4 className="font-['Playfair_Display'] text-lg text-white tracking-wide">
                      {section.title}
                    </h4>
                    <div className="flex-1 h-px bg-[#1a2030] ml-4" />
                    <span className="text-gray-600 text-xs font-mono">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                  </div>

                  {/* Section Content */}
                  <div className="px-6 py-6">
                    <div className="space-y-3">
                      {contentLines.map((line, lineIndex) => {
                        // Check if line looks like a subsection title
                        const isSubtitle = line.length < 60 && 
                          (line.includes(':') && line.indexOf(':') < 30) ||
                          /^[A-Z][^.!?]*$/.test(line);
                        
                        // Check if it's a list item indicator
                        const isListItem = contentLines.length > 3 && !isSubtitle;

                        if (isSubtitle && line.includes(':')) {
                          const [label, value] = line.split(':');
                          return (
                            <div key={lineIndex} className="flex gap-2 py-2">
                              <span className="text-[#C9A24A] font-medium min-w-fit">{label.trim()}:</span>
                              <span className="text-gray-300">{value?.trim()}</span>
                            </div>
                          );
                        }

                        return (
                          <p 
                            key={lineIndex} 
                            className={`text-gray-300 leading-relaxed ${
                              isListItem ? 'pl-4 border-l-2 border-[#1a2030]' : ''
                            }`}
                          >
                            {line}
                          </p>
                        );
                      })}
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </div>

          {/* ===== OFFICIAL SOURCES ===== */}
          {caseData.sources && caseData.sources.length > 0 && (
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              viewport={{ once: true }}
              className="mt-12 bg-[#0a0c10] border border-[#1a2030]"
            >
              <div className="flex items-center gap-4 px-6 py-4 border-b border-[#1a2030] bg-[#0d1015]">
                <div className="w-10 h-10 flex items-center justify-center bg-[#C9A24A]/10 rounded-sm">
                  <Globe className="w-5 h-5 text-[#C9A24A]" />
                </div>
                <h4 className="font-['Playfair_Display'] text-lg text-white tracking-wide">
                  Fontes Públicas Consultadas
                </h4>
              </div>
              <div className="p-6">
                <div className="grid gap-3">
                  {caseData.sources.map((source, index) => (
                    <a
                      key={index}
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-start gap-4 p-4 bg-[#0d1015] border border-[#1a2030] hover:border-[#C9A24A]/30 transition-colors group"
                    >
                      <div className="w-8 h-8 flex items-center justify-center bg-[#1a2030] rounded-sm text-gray-500 text-xs font-mono group-hover:bg-[#C9A24A]/10 group-hover:text-[#C9A24A] transition-colors">
                        {String(index + 1).padStart(2, '0')}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-white group-hover:text-[#C9A24A] transition-colors block mb-1">
                          {source.title}
                        </span>
                        <span className="text-gray-500 text-sm block truncate">
                          {source.url}
                        </span>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            </motion.section>
          )}

          {/* ===== CTA SECTION ===== */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            viewport={{ once: true }}
            className="mt-16 p-8 bg-[#0a0c10] border border-[#C9A24A]/20"
          >
            <div className="text-center mb-8">
              <h4 className="font-['Playfair_Display'] text-2xl text-white mb-2">
                Próximos Passos
              </h4>
              <p className="text-gray-400 max-w-xl mx-auto">
                Selecione o serviço desejado e entre em contato com nossa equipe técnica 
                para dar continuidade ao seu processo.
              </p>
            </div>

            {/* Coupon Display */}
            {caseData.coupon_code && (
              <div className="flex items-center justify-center gap-4 mb-8 p-4 bg-[#050608] border border-[#1a2030]">
                <span className="text-gray-500 text-sm uppercase tracking-wider">Seu Código</span>
                <button 
                  onClick={copyCouponCode}
                  className="text-[#C9A24A] font-mono text-xl font-bold hover:text-white transition-colors"
                >
                  {caseData.coupon_code}
                </button>
              </div>
            )}

            {/* Service Selection */}
            <div className="max-w-md mx-auto mb-8">
              <label className="text-gray-500 text-sm uppercase tracking-wider block mb-3 text-center">
                Serviço Pretendido
              </label>
              <Select value={selectedService} onValueChange={setSelectedService}>
                <SelectTrigger className="w-full h-14 bg-[#050608] border-[#1a2030] text-white text-center">
                  <SelectValue placeholder="Selecionar serviço" />
                </SelectTrigger>
                <SelectContent className="bg-[#0a0c10] border-[#1a2030]">
                  {services.map((service) => (
                    <SelectItem 
                      key={service.code} 
                      value={service.code}
                      className="text-white hover:bg-[#C9A24A]/10"
                    >
                      {service.name_pt}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                data-testid="support-btn"
                onClick={() => handleWhatsAppClick('support')}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 py-4 bg-transparent border border-[#1a2030] text-white font-semibold uppercase tracking-wider text-sm hover:border-[#C9A24A] hover:text-[#C9A24A] transition-colors"
              >
                <Phone className="w-5 h-5" />
                Falar com Suporte
              </button>
              <button
                data-testid="advance-btn"
                onClick={() => handleWhatsAppClick('service')}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#C9A24A] text-[#050608] font-semibold uppercase tracking-wider text-sm hover:bg-white transition-colors"
              >
                <MessageCircle className="w-5 h-5" />
                Avançar com Serviço
              </button>
            </div>
          </motion.section>

          {/* ===== LEGAL DISCLAIMER ===== */}
          <div className="mt-16 pt-8 border-t border-[#1a2030]">
            <div className="flex items-start gap-4">
              <Shield className="w-5 h-5 text-gray-600 mt-1 flex-shrink-0" />
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-gray-500">Aviso Legal:</strong> Este documento é elaborado com base nas informações 
                fornecidas pelo cliente e em legislação vigente na data de emissão. Não constitui aconselhamento 
                jurídico formal nem garantia de aprovação de qualquer procedimento administrativo. 
                Recomenda-se consultar profissional habilitado para validação e execução dos procedimentos indicados. 
                FORTUNATO LEGISLAI não se responsabiliza por decisões tomadas exclusivamente com base neste relatório.
              </p>
            </div>
          </div>

          {/* Footer */}
          <footer className="mt-12 pt-8 border-t border-[#1a2030] text-center">
            <LogoFull size="small" className="mx-auto mb-4 opacity-50" />
            <p className="text-gray-600 text-xs">
              © {new Date().getFullYear()} FORTUNATO LEGISLAI · Documento Confidencial
            </p>
          </footer>
        </main>
      </div>
    </div>
  );
}
